import {StatusType} from '@/helpers/constants';
import {sortBy} from '@/helpers/utils';
import store from '..';

export const StudyState = Object.freeze({
   ACTIVE: 'ACTIVE',
   LOCKED: 'LOCKED',
   INACTIVE: 'INACTIVE',
});

export const StudyStage = Object.freeze({
   NO_OPEN_STUDY: 'NO_OPEN_STUDY',
   CLIENT_INFO_COLLECTION: 'CLIENT_INFO_COLLECTION',
   RDIG_STUDY_SETUP: 'RDIG_STUDY_SETUP',
   CLIENT_DATA_COLLECTION: 'CLIENT_DATA_COLLECTION',
   RDIG_DATA_REVIEW: 'RDIG_DATA_REVIEW',
   RDIG_CREDIT_CALCULATION: 'RDIG_CREDIT_CALCULATION',
   DELIVERED: 'DELIVERED',
});

/**
 * Priority order for auto-selecting the study that most needs attention when a
 * user logs in with multiple open studies. Lower number = higher priority.
 * Differs from natural stage progression: CLIENT_DATA_COLLECTION is promoted
 * above both setup stages since it represents outstanding customer data entry.
 */
export const STUDY_STAGE_PRIORITY = Object.freeze({
   [StudyStage.CLIENT_DATA_COLLECTION]: 0,
   [StudyStage.CLIENT_INFO_COLLECTION]: 1,
   [StudyStage.RDIG_STUDY_SETUP]: 2,
   [StudyStage.RDIG_DATA_REVIEW]: 3,
   [StudyStage.RDIG_CREDIT_CALCULATION]: 4,
   [StudyStage.DELIVERED]: 5,
});

/**
 * Given a list of open studies, pick the one that most needs the user's attention,
 * per STUDY_STAGE_PRIORITY (lower number wins). Ties are broken by the most recent
 * study period start date (later wins), then by study id (higher wins) as a final
 * deterministic fallback.
 * @param {Study[]} openStudies
 * @returns {Study|null}
 */
export function selectMostActionableStudy(openStudies) {
   if (!openStudies || openStudies.length === 0) {
      return null;
   }
   if (openStudies.length === 1) {
      return openStudies[0];
   }

   const priorityOf = (study) => STUDY_STAGE_PRIORITY[study.stage] ?? Infinity;
   const latestStartTime = (study) => study.lastPeriod?.startDate?.getTime() ?? -Infinity;

   return openStudies.reduce((best, candidate) => {
      const priorityDiff = priorityOf(candidate) - priorityOf(best);
      if (priorityDiff < 0) return candidate;
      if (priorityDiff > 0) return best;

      const dateDiff = latestStartTime(candidate) - latestStartTime(best);
      if (dateDiff > 0) return candidate;
      if (dateDiff < 0) return best;

      return candidate.id > best.id ? candidate : best;
   });
}

export class Study {
   constructor({
      id = null,
      companyId,
      label = null,
      description = null,
      state = StudyState.ACTIVE,
      speriods = [],
      cloudcomputing = null,
      cloudcomputingAssignments = [],
      contractors = null,
      contractorAssignments = [],
      supplies = null,
      suppliesAssignments = [],
      employees = null,
      employeeAssignments = [],
      questionnaires = null,
      stage = StudyStage.CLIENT_INFO_COLLECTION,

      studySetupCompleted = null,
      electing280CBool = false,
   } = {}) {
      this.id = id;
      this.companyId = companyId;
      this.label = label;
      this.description = description;
      this.electing_280c_bool = electing280CBool;
      this.state = state;

      this.cloudcomputing = cloudcomputing;
      this.cloudcomputingAssignments = cloudcomputingAssignments;
      this.contractors = contractors;
      this.contractorAssignments = contractorAssignments;
      this.supplies = supplies;
      this.suppliesAssignments = suppliesAssignments;
      this.employees = employees;
      this.employeeAssignments = employeeAssignments;
      this.questionnaires = questionnaires;
      this.stage = stage;
      this.studySetupCompleted = studySetupCompleted;

      this.periods = speriods.map((period) => {
         return period instanceof StudyPeriod ? period : new StudyPeriod({...period, studyId: id});
      });
      this.sortPeriods();
   }

   get firstPeriod() {
      return this.periods[0];
   }

   get lastPeriod() {
      return this.periods[this.periods.length - 1];
   }

   get isOpen() {
      return this.state === StudyState.ACTIVE;
   }

   get status() {
      switch (this.state) {
         case StudyState.ACTIVE:
            return {status: StatusType.COMPLETE, text: 'Open'};
         case StudyState.LOCKED:
            return {status: StatusType.IN_PROGRESS, text: 'Locked'};
         case StudyState.INACTIVE:
            return {status: StatusType.INCOMPLETE, text: 'Closed'};
         default:
            return {status: StatusType.NA};
      }
   }

   get humanPeriodLabels() {
      const periodCount = this.periods.length;
      if (periodCount < 3) {
         return this.periods.map((period) => period.label).join(', ');
      } else {
         return `${this.periods[0].label}, ..., ${this.periods[periodCount - 1].label}`;
      }
   }

   sortPeriods() {
      this.periods.sort(sortBy('startDate'));
   }

   addPeriod(period) {
      this.periods.push(period);
      this.sortPeriods();
   }

   removePeriod(period) {
      const idx = this.periods.findIndex((p) => p.isEqual(period));
      if (idx > -1) {
         this.periods.splice(idx, 1);
      }
   }

   removePeriodId(periodId) {
      const idx = this.periods.findIndex((p) => p.id === periodId);
      if (idx > -1) {
         this.periods.splice(idx, 1);
      }
   }

   export({periods = true} = {}) {
      const study = {
         label: this.label,
         description: this.description,
         electing_280c_bool: this.electing_280c_bool,
      };

      if (periods) {
         study.periods = this.periods.map((period) => period.export());
      }

      return study;
   }
}

export class StudyPeriod {
   constructor({id = null, period = {lower: null, upper: null}, label = null, studyId} = {}) {
      this.id = id;
      this.period = period;
      this.label = label;
      this.studyId = studyId;
   }

   static tableField({key = 'period', label = 'Time Period', sortable = true} = {}) {
      return {
         key,
         label,
         sortable,
         class: 'study-period-table-cell',
         formatter: (period) => period.label,
         sortByFormatted: (period) => period.startDate,
      };
   }

   isEqual(other) {
      return this.period.lower === other.period.lower && this.period.upper === other.period.upper;
   }

   isBefore(other) {
      return this.startDate < other.startDate;
   }

   /**
    * Does this study period contain the given date?
    * @param {String|Date} date
    */
   containsDate(date) {
      if (!(this.startDate && this.endDate)) {
         return false;
      }
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      const check = new Date(date);
      return start < check && check < end;
   }

   /**
    * Does this study period's date range overlap the given date range?
    * @param {String|Date} startDate
    * @param {String|Date} endDate
    */
   overlaps(startDate, endDate) {
      const thisStart = new Date(this.startDate);
      const thisEnd = new Date(this.endDate);
      const otherStart = new Date(startDate);
      const otherEnd = new Date(endDate);
      return thisStart < otherEnd && thisEnd > otherStart;
   }

   get study() {
      return store.getters['companies/studyMap'][this.studyId];
   }

   get startDate() {
      if (!this.period.lower) {
         return this.period.lower;
      }
      const [year, month, day] = this.period.lower.split('-');
      return new Date(Date.UTC(year, month - 1, day));
   }
   set startDate(val) {
      if (val instanceof Date) {
         // Convert to 'YYYY-MM-DD', accounting for timezone offset
         const offset = val.getTimezoneOffset();
         val = new Date(val.getTime() - offset * 60 * 1000);
         val = val.toISOString().split('T')[0];
      }
      this.period.lower = val;
   }

   get endDate() {
      if (!this.period.upper) {
         return this.period.upper;
      }
      const [year, month, day] = this.period.upper.split('-');
      return new Date(Date.UTC(year, month - 1, day));
   }
   set endDate(val) {
      if (val instanceof Date) {
         // Convert to 'YYYY-MM-DD', accounting for timezone offset
         const offset = val.getTimezoneOffset();
         val = new Date(val.getTime() - offset * 60 * 1000);
         val = val.toISOString().split('T')[0];
      }
      this.period.upper = val;
   }

   copy() {
      return new StudyPeriod({
         id: this.id,
         period: {...this.period},
         label: this.label,
         studyId: this.studyId,
      });
   }

   export() {
      return {
         period: this.period,
         label: this.label,
      };
   }
}
